'use client'

import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { Media } from '@/components/Media'
import { getLinkHref } from '@/components/Link'
import type {
  Media as MediaType,
  Page,
  Project,
  Product,
  Job,
  File as FileDoc,
} from '@/payload-types'

interface LinkData {
  type?: ('reference' | 'custom' | 'file' | 'phone' | 'email') | null
  reference?:
    | { relationTo: 'pages'; value: Page | string }
    | { relationTo: 'projects'; value: Project | string }
    | { relationTo: 'products'; value: Product | string }
    | { relationTo: 'jobs'; value: Job | string }
    | null
  file?: FileDoc | string | null
  url?: string | null
  phone?: string | null
  email?: string | null
  label?: string | null
  openInNewTab?: boolean | null
  anchor?: string | null
}

interface ImagePane {
  id?: string | null
  title: string
  subtitle?: string | null
  image?: MediaType | string | null
  link?: LinkData | null
}

interface ApprenticeshipSplitVideosClientProps {
  videoPanes: ImagePane[]
  settings?: {
    backgroundShade?: 'white' | 'neutral-50' | 'neutral-100' | null
    isDarkMode?: boolean | null
    enablePadding?: boolean | null
    padding?: 'sm' | 'md' | 'lg' | 'xl' | null
    container?: 'container' | 'narrow' | 'full' | null
  } | null
}

export default function ApprenticeshipSplitVideosClient({
  videoPanes,
  settings,
}: ApprenticeshipSplitVideosClientProps) {
  const [hovered, setHovered] = useState<string | null>(null)
  const [isMdUp, setIsMdUp] = useState(false)

  useEffect(() => {
    const mql = window.matchMedia('(min-width: 768px)')
    const onChange = (e: MediaQueryListEvent) => setIsMdUp(e.matches)
    setIsMdUp(mql.matches)
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [])

  if (!videoPanes || videoPanes.length === 0) {
    return null
  }

  const panes = videoPanes.slice(0, 2)

  return (
    <div className="relative w-full" onMouseLeave={() => setHovered(null)}>
      <div className="flex flex-col md:flex-row gap-4 md:h-[28rem] lg:h-[34rem]">
        {panes.map((pane, index) => {
          const paneId = pane.id || `pane-${index}`
          const isHovered = hovered === paneId
          const isActive = isHovered || hovered === null
          const targetWidth = isMdUp
            ? hovered
              ? hovered === paneId
                ? '100%'
                : '0%'
              : '50%'
            : '100%'

          const href = getLinkHref(pane.link)
          const openInNewTab = pane.link?.openInNewTab

          const content = (
            <motion.div
              key={paneId}
              className="relative overflow-hidden bg-dark text-text-white h-[18rem] md:h-auto"
              onMouseEnter={() => setHovered(paneId)}
              animate={{ width: targetWidth }}
              initial={false}
              transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
              style={{ willChange: 'width' }}
            >
              {/* Image */}
              {typeof pane.image === 'object' && pane.image !== null && (
                <motion.div
                  className="absolute inset-0 h-full w-full"
                  style={{ position: 'absolute' }}
                  animate={{
                    scale: hovered === paneId ? 1.03 : 1.0,
                    opacity: isActive ? 1 : 0,
                  }}
                  transition={{
                    duration: 0.6,
                    ease: [0.25, 0.46, 0.45, 0.94],
                  }}
                >
                  <div className="relative w-full h-full">
                    <Media
                      resource={pane.image}
                      fill
                      pictureClassName="absolute inset-0 w-full h-full"
                      imgClassName="object-cover"
                      size="(max-width: 768px) 100vw, 50vw"
                    />
                  </div>
                </motion.div>
              )}

              {/* Dark gradient for text readability */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none" />

              {/* Content overlay */}
              <div className="absolute inset-0 flex items-end pointer-events-none">
                <div className="p-6 md:p-8">
                  {pane.subtitle && (
                    <motion.div
                      className="text-sm tracking-wide text-text-white/80"
                      animate={{
                        opacity: isHovered ? 1 : 0,
                        y: isHovered ? 0 : 8,
                      }}
                      initial={false}
                      transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                    >
                      {pane.subtitle}
                    </motion.div>
                  )}
                  <h3 className="mt-2 text-2xl md:text-4xl font-bold uppercase">{pane.title}</h3>
                  {href && (
                    <span className="mt-3 inline-flex items-center gap-2 font-ui font-medium text-sm md:text-base text-white uppercase">
                      Zur Stellenbeschreibung
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        className="transition-transform duration-300 group-hover:translate-x-1"
                      >
                        <path
                          d="M9 18L15 12L9 6"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  )}
                </div>
              </div>
            </motion.div>
          )

          if (href) {
            return (
              <a
                key={paneId}
                href={href}
                className="group block md:contents"
                {...(openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                aria-label={pane.title}
              >
                {content}
              </a>
            )
          }

          return content
        })}
      </div>
    </div>
  )
}
